import { 
  HttpClient, 
  httpClient, 
  isURL, 
  retryPost, 
  invariant, 
  generateRandomString,
  Value,
  IValue,
  ReferrerCode,
  getUrlSearchParams,
} from '@spindl-xyz/sdk-core';
import type {
  BeaconOptions,
  SpindlInstance,
} from '@spindl-xyz/sdk-core';

import { BeaconPayload, SpindlBaseOptions, SpindlOptions } from './types';
import { version } from '../package.json';

const defaultOtions: SpindlBaseOptions = {
  API_URL: 'https://spindl.link',
  BACKEND_URL: 'https://api.spindl.xyz/v1',
  restricted: false,
};

export class SpindlClient implements SpindlInstance {
  // override for tests
  public httpClient: HttpClient = httpClient;
  private options: SpindlOptions;

  private version = version;
  private sessionId: IValue<string>;
  private referrerCode: IValue<string>;
  
  constructor() {
    this.sessionId = new Value({
      name: 'sessionId',
      broadcast: true,
      persist: 'session',
      initialValue: () => generateRandomString(),
    });
    this.referrerCode = new Value({
      name: 'referrerCode',
      broadcast: true,
      persist: 'local',
      onSet: (curr) => this.getReferrer() ?? curr,
    });
  }

  configure(config: SpindlOptions) {
    const options = {
      ...defaultOtions,
      ...this.options,
      ...config,
    };
    this.validateConfiguration(options);
    this.options = options;
  }

  attribute(address: string) {
    const headers: Record<string, string> = {};
    if (this.options.API_KEY && typeof this.options.API_KEY === 'string') {
      headers['X-API-Key'] = this.options.API_KEY;
    }
    const sourceUrl = window?.location?.href;
    const referrer = window?.document?.referrer;
    return this.sendBeacon({
      type: 'WALLET_CONNECTED',
      data: { address, sourceUrl, referrer },
    });
  }

  pageView() {
    // only want to send in a browser environmnet
    if (typeof window === 'undefined') {
      return;
    }

    const sourceUrl = window?.location?.href;
    const referrer = window?.document?.referrer;
    return this.sendBeacon({
      type: 'PAGE_VIEW',
      data: { sourceUrl, referrer },
    });
  }

  enableAutoPageViews() {
    // initial page view
    this.pageView();

    const track = () => this.pageView();
    const original = history.pushState;

    // monkey patch pushState
    if (original) {
      history.pushState = function (data, unused, url) {
        original.call(this, data, unused, url);
        track();
      };
      addEventListener('popstate', track);
    }

    return function () {
      if (original) {
        history.pushState = original;
        removeEventListener('popstate', track);
      }
    };
  }

  async getReferrerAttribution(address: string): Promise<string> {
    const url = new URL(`${this.options.BACKEND_URL}/referrer-attribution/${address}`);
    if (this.referrerCode.Value) {
      url.searchParams.set('ref', this.referrerCode.Value);
    }
    const response = await this.httpClient({
      url: url.toString(),
      headers: {
        'X-API-Key': this.options.API_KEY,
      },
      method: 'GET',
    });
    if (!response) {
      return undefined;
    }
    const body = await response.json();
    return body.attribution;
  }

  async createReferrerCode(address: string, code?: string): Promise<string> {
    const response = await this.httpClient({
      url: `${this.options.BACKEND_URL}/referrer-codes`,
      headers: {
        'X-API-Key': this.options.API_KEY,
        'content-type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ address, code }),
    });
    if (!response) {
      return undefined;
    }
    const body = await response.json();
    return body.code;
  }

  validateReferrerCode(slug?: string) {
    return typeof slug === "string" && /^([a-z0-9_]){4,20}$/.test(slug);
  }

  async checkReferrerCode(code: string): Promise<boolean> {
    const response = await this.httpClient({
      url: `${this.options.BACKEND_URL}/referrer-codes/check-availability`,
      headers: {
        'X-API-Key': this.options.API_KEY,
        'content-type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    if (!response) {
      return false;
    }
    const body = await response.json();
    return body.available;
  }

  async getReferrerCode(address: string): Promise<string> {
    if (!address) return;
    const referrerCodes = await this.getReferrerCodes({ address });
    if (referrerCodes?.length) {
      return referrerCodes[0].code
    }
  }
  
  async getReferrerAddress(code: string): Promise<string> {
    if (!code) return;
    const referrerCodes = await this.getReferrerCodes({ code });
    if (referrerCodes?.length) {
      return referrerCodes[0].address
    }
  }

  async getReferrerCodes(filter: Partial<ReferrerCode> = {}): Promise<ReferrerCode[]> {
    const url = new URL(`${this.options.BACKEND_URL}/referrer-codes`);
    if (filter.address) {
      url.searchParams.set('address', filter.address);
    }
    if (filter.code) {
      url.searchParams.set('code', filter.code);
    }
    const response = await this.httpClient({
      url: url.toString(),
      headers: {
        'X-API-Key': this.options.API_KEY,
      },
      method: 'GET',
    });
    if (response) {
      return await response.json();
    }
  }

  private async sendBeacon(beacon: BeaconOptions) {
    invariant(
      typeof window !== 'undefined' && typeof window.fetch !== 'undefined',
      'SpindlClient: requires fetch globally'
    );

    this.validateApiKey();

    const headers: Record<string, string> = {
      'X-API-Key': this.options.API_KEY,
    };

    const payload: BeaconPayload = {
      ...beacon,
      metadata: {
        version: this.version,
        sessionId: this.sessionId.Value,
        restricted: true,
        referrerCode: this.referrerCode.Value,
      },
    };

    const response = await retryPost(() =>
      this.httpClient({
        url: `${this.options.API_URL}/events`,
        headers: {
          ...headers,
          'content-type': 'application/json',
        },
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(payload),
      })
    );

    return Boolean(response);
  }

  validateApiKey(key?: string) {
    const apiKey = key ?? this.options.API_KEY;
    invariant(
      typeof apiKey === 'string' && apiKey.length > 0,
      'SpindlClient: Missing "API_KEY"'
    );
  }

  validateConfiguration(config: SpindlOptions) {
    invariant(isURL(config.API_URL), 'SpindlClient: Unsupported "API_URL"');
    this.validateApiKey(config.API_KEY);
  }

  private getReferrer(): string {
    const params = getUrlSearchParams();
    if (params && params.has('ref')) {
      return params.get('ref');
    }
    return;
  }
}

export default SpindlClient;
